import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { COMMON_TIMEZONES } from '@/lib/serverTime';
import {
  api,
  type SelfReportMe,
  type SelfReportMeEventCounts,
  type SelfReportMeEventRow,
  type SelfReportMeSpecialCounts,
  type SelfReportMeVsDay,
  type SelfReportMeVsWeek,
  type SelfReportRange,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { AlertTriangle, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import PlayerHubHeader from '@/components/PlayerHubHeader';
import { usePlayerAlliance } from '@/lib/usePlayerAlliance';
import PushEnablementPrompt from '@/components/PushEnablementPrompt';
import VsWeekBox from '@/components/VsWeekBox';
import { DAILY_RED_ABSOLUTE_M } from '@/lib/vsSeverity';

const RANGE_OPTIONS: { value: SelfReportRange; label: string }[] = [
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'all', label: 'All time' },
];

function currentMondayIso(): string {
  const now = new Date();
  const day = now.getUTCDay();
  // JS Sunday = 0 → treat as day 7 so Monday is 1 and Sunday is 7.
  const offset = day === 0 ? 6 : day - 1;
  const monday = new Date(now.getTime() - offset * 24 * 60 * 60 * 1000);
  return monday.toISOString().slice(0, 10);
}

const TILE_KEYS: Array<{
  key: keyof Omit<SelfReportMeEventCounts, 'slot'>;
  label: string;
}> = [
  { key: 'registered', label: 'Registered' },
  { key: 'picked', label: 'Picked' },
  { key: 'notPicked', label: 'Not picked' },
  { key: 'starter', label: 'Starter' },
  { key: 'sub', label: 'Sub' },
  { key: 'noShow', label: 'No-show' },
  { key: 'late', label: 'Late' },
];

export function usePwaManifest(token: string, playerLabel: string) {
  const alliance = usePlayerAlliance(token);
  const allianceLabel = alliance ? alliance.tag || alliance.name : '';
  const iconUrl = alliance?.logoUrl ?? '/favicon.svg';
  useEffect(() => {
    if (!token || !alliance) return;
    const scope = `/players/${token}/`;
    const startUrl = `/players/${token}/self-report`;
    const label = playerLabel || 'Player';
    const manifest = {
      name: `${allianceLabel} Hub — ${label}`,
      short_name: `${allianceLabel} Hub`,
      description: `Personal stats for the ${allianceLabel} alliance in Last War.`,
      start_url: startUrl,
      scope,
      id: scope,
      display: 'standalone',
      orientation: 'portrait',
      background_color: '#020617',
      theme_color: '#4f46e5',
      icons: [
        {
          src: iconUrl,
          sizes: 'any',
          type: alliance.logoUrl ? 'image/png' : 'image/svg+xml',
          purpose: 'any',
        },
      ],
    };
    const blob = new Blob([JSON.stringify(manifest)], {
      type: 'application/manifest+json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.setAttribute('data-dynamic-manifest', 'player-hub');
    link.href = url;
    document.head.appendChild(link);
    return () => {
      link.remove();
      URL.revokeObjectURL(url);
    };
  }, [token, playerLabel, alliance, allianceLabel, iconUrl]);
}

export default function PlayerHubMe() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? '';
  const [range, setRange] = useState<SelfReportRange>('90d');
  const meQuery = useQuery({
    queryKey: ['self-report-me', token, range],
    queryFn: () => api.getSelfReportMe(token, range),
    retry: false,
    enabled: !!token,
  });

  const playerLabel =
    meQuery.data?.player.displayName?.trim() || meQuery.data?.player.name || '';
  usePwaManifest(token, playerLabel);

  if (meQuery.isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="mx-auto max-w-md px-4 py-10 text-slate-400">Loading…</div>
      </div>
    );
  }
  if (meQuery.isError || !meQuery.data) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="mx-auto max-w-md px-4 py-10">
          <h1 className="mb-2 text-xl font-semibold">Link not valid</h1>
          <p className="text-sm text-slate-400">
            This link is invalid or has been revoked. Please ask an R4/R5 to
            send you a fresh one.
          </p>
        </div>
      </div>
    );
  }

  const data = meQuery.data;
  const { player, events, vs } = data;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <PushEnablementPrompt context={{ kind: 'player', token }} />
      <PlayerHubHeader
        token={token}
        playerLabel={player.displayName?.trim() || player.name}
        playerRank={player.rank}
        subtitle="Event history"
        upcomingEvents={data.upcomingEvents}
      />
      <main className="mx-auto max-w-md space-y-6 px-4 py-6">
        <TimezoneCard token={token} current={player.timezone} />

        <RangePicker range={range} onChange={setRange} />

        <EventCountsCard
          title="Canyon Storm"
          total={events.totals.canyon_storm}
          counts={events.counts.canyon_storm}
        />
        <EventCountsCard
          title="Desert Storm"
          total={events.totals.desert_storm}
          counts={events.counts.desert_storm}
        />
        <SpecialEventCountsCard counts={events.special} />

        <EventHistorySection rows={events.history} />

        <VsHistorySection weeks={vs.weeks} policy={vs.policy} />
      </main>
    </div>
  );
}

function TimezoneCard({
  token,
  current,
}: {
  token: string;
  current: string | null;
}) {
  const qc = useQueryClient();
  const browserTz =
    Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<string>(current ?? browserTz ?? '');
  const [customMode, setCustomMode] = useState<boolean>(() => {
    if (!current) return false;
    return !COMMON_TIMEZONES.includes(current);
  });

  const save = useMutation({
    mutationFn: (tz: string | null) =>
      api.updateSelfReportProfile(token, { timezone: tz }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['self-report-me', token] });
      setEditing(false);
    },
  });

  const localSample = value
    ? formatTimeInZone(new Date(), value)
    : null;

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Your timezone
          </div>
          <div className="mt-0.5 text-sm text-slate-200">
            {current ?? (
              <span className="text-amber-300">Not set</span>
            )}
          </div>
          {current && (
            <div className="text-[11px] text-slate-500">
              Local now: {formatTimeInZone(new Date(), current)}
            </div>
          )}
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded border border-slate-700 bg-slate-950 px-2.5 py-1 text-xs text-slate-200 hover:border-slate-500"
          >
            {current ? 'Change' : 'Set'}
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-3 space-y-2">
          {!customMode ? (
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              style={{
                color: '#e2e8f0',
                backgroundColor: '#020617',
                fontSize: '16px',
              }}
              className="w-full rounded border border-slate-700 px-2 py-2 text-sm"
            >
              <option value="">Select a timezone…</option>
              {browserTz && !COMMON_TIMEZONES.includes(browserTz) && (
                <option value={browserTz}>{browserTz} (device)</option>
              )}
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. America/Bogota"
              style={{
                color: '#e2e8f0',
                backgroundColor: '#020617',
                fontSize: '16px',
              }}
              className="w-full rounded border border-slate-700 px-2 py-2 text-sm"
            />
          )}
          <div className="flex items-center justify-between text-[11px] text-slate-500">
            <button
              type="button"
              className="underline hover:text-slate-300"
              onClick={() => setCustomMode((m) => !m)}
            >
              {customMode ? 'Use list' : 'Enter IANA name manually'}
            </button>
            {localSample && (
              <span className="tabular-nums">Preview: {localSample}</span>
            )}
          </div>
          {browserTz && value !== browserTz && (
            <div className="text-[11px] text-slate-500">
              Device says: {browserTz}
              <button
                type="button"
                className="ml-2 underline hover:text-slate-300"
                onClick={() => {
                  setValue(browserTz);
                  setCustomMode(!COMMON_TIMEZONES.includes(browserTz));
                }}
              >
                Use device
              </button>
            </div>
          )}
          {save.isError && (
            <div className="text-xs text-rose-300">
              {(save.error as Error).message}
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="flex-1 rounded border border-slate-700 bg-slate-950 py-2 text-xs text-slate-300"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={save.isPending || !value.trim()}
              onClick={() => save.mutate(value.trim())}
              className="flex-1 rounded bg-indigo-600 py-2 text-xs font-medium hover:bg-indigo-500 disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function formatTimeInZone(d: Date, tz: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    return '—';
  }
}

function RangePicker({
  range,
  onChange,
}: {
  range: SelfReportRange;
  onChange: (r: SelfReportRange) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-slate-800 bg-slate-900/60 p-0.5 text-xs">
      {RANGE_OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded px-3 py-1 transition',
            range === o.value
              ? 'bg-indigo-600 text-white'
              : 'text-slate-400 hover:text-white',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function EventCountsCard({
  title,
  total,
  counts,
}: {
  title: string;
  total: number;
  counts: SelfReportMeEventCounts;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400">
          {title}
        </h2>
        <span className="text-xs text-slate-500">{total} events</span>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {TILE_KEYS.map((k) => (
          <Tile
            key={k.key}
            label={k.label}
            value={counts[k.key]}
            pct={pctFor(k.key, counts, total)}
            tone={toneFor(k.key, counts[k.key])}
          />
        ))}
        <Tile
          label="Slot A/B"
          value={
            <span className="tabular-nums">
              {counts.slot.A}
              <span className="text-slate-500">/</span>
              {counts.slot.B}
            </span>
          }
          pct={
            counts.picked > 0
              ? `${Math.round(
                  ((counts.slot.A + counts.slot.B) / counts.picked) * 100,
                )}%`
              : null
          }
        />
      </div>
    </section>
  );
}

function pctFor(
  key: keyof Omit<SelfReportMeEventCounts, 'slot'>,
  counts: SelfReportMeEventCounts,
  total: number,
): string | null {
  const round = (n: number) => `${Math.round(n * 100)}%`;
  if (total === 0) return null;
  switch (key) {
    case 'registered':
      return round(counts.registered / total);
    case 'picked':
      return counts.registered > 0 ? round(counts.picked / counts.registered) : null;
    case 'notPicked':
      return counts.registered > 0 ? round(counts.notPicked / counts.registered) : null;
    case 'starter':
    case 'sub':
      return counts.picked > 0 ? round(counts[key] / counts.picked) : null;
    case 'noShow':
    case 'late':
      return counts.picked > 0 ? round(counts[key] / counts.picked) : null;
    default:
      return null;
  }
}

function toneFor(
  key: keyof Omit<SelfReportMeEventCounts, 'slot'>,
  value: number,
): TileTone {
  if (value <= 0) return 'default';
  if (key === 'noShow') return 'red';
  if (key === 'late') return 'orange';
  return 'default';
}

type TileTone = 'default' | 'red' | 'orange';

function Tile({
  label,
  value,
  pct,
  tone = 'default',
}: {
  label: string;
  value: number | React.ReactNode;
  pct?: string | null;
  tone?: TileTone;
}) {
  const toneClass =
    tone === 'red'
      ? 'border-red-800/70 bg-red-950/40'
      : tone === 'orange'
        ? 'border-amber-800/70 bg-amber-950/40'
        : 'border-slate-800 bg-slate-900/40';
  const valueClass =
    tone === 'red'
      ? 'text-red-100'
      : tone === 'orange'
        ? 'text-amber-100'
        : 'text-slate-100';
  const labelClass =
    tone === 'red'
      ? 'text-red-300'
      : tone === 'orange'
        ? 'text-amber-300'
        : 'text-slate-500';
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border px-2 py-2',
        toneClass,
      )}
    >
      <div className={cn('text-lg font-semibold tabular-nums', valueClass)}>
        {value}
      </div>
      {pct && (
        <div className="text-[10px] font-medium tabular-nums text-slate-400">
          {pct}
        </div>
      )}
      <div
        className={cn(
          'mt-0.5 text-[10px] uppercase tracking-wider',
          labelClass,
        )}
      >
        {label}
      </div>
    </div>
  );
}

function SpecialEventCountsCard({
  counts,
}: {
  counts: SelfReportMeSpecialCounts;
}) {
  const total = counts.events;
  const pct = (n: number, d: number) =>
    d > 0 ? `${Math.round((n / d) * 100)}%` : null;
  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400">
          Special events
        </h2>
        <span className="text-xs text-slate-500">{total} events</span>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        <Tile
          label="Assigned"
          value={counts.assigned}
          pct={pct(counts.assigned, total)}
        />
        <Tile
          label="Showed"
          value={counts.showed}
          pct={pct(counts.showed, counts.assigned)}
        />
        <Tile
          label="Rulebreaker"
          value={counts.rulebreaker}
          pct={pct(counts.rulebreaker, counts.assigned)}
          tone={counts.rulebreaker > 0 ? 'orange' : 'default'}
        />
        <Tile
          label="No-show"
          value={counts.noShow}
          pct={pct(counts.noShow, counts.assigned)}
          tone={counts.noShow > 0 ? 'red' : 'default'}
        />
        {counts.pending > 0 && (
          <Tile
            label="Pending"
            value={counts.pending}
            pct={pct(counts.pending, counts.assigned)}
          />
        )}
      </div>
    </section>
  );
}

function EventHistorySection({ rows }: { rows: SelfReportMeEventRow[] }) {
  const [open, setOpen] = useState(false);
  const count = rows.length;
  return (
    <section className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2 text-left"
      >
        <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-400">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Event history
        </span>
        <span className="text-[11px] text-slate-500">{count} events</span>
      </button>
      {open && <EventHistoryList rows={rows} />}
    </section>
  );
}

const DAILY_TRACKING_STARTS = '2026-08-23';

const EMPTY_VS_DAYS: SelfReportMeVsDay[] = Array.from({ length: 6 }, (_, i) => ({
  dayIndex: i,
  scoreTenthsM: null,
  dayTwentiethTenthsM: null,
  dailyWin: false,
  rank: null,
  totalPlayers: 0,
  othersAvgTenthsM: null,
}));

function VsHistorySection({
  weeks,
  policy,
}: {
  weeks: SelfReportMeVsWeek[];
  policy: SelfReportMe['vs']['policy'];
}) {
  const currentMonday = currentMondayIso();
  const { pastWithDays, pastWeeklyOnly } = useMemo(() => {
    const rest = weeks.filter((w) => w.weekStart !== currentMonday);
    const withDays = rest.filter((w) => w.days != null);
    const weeklyOnly = rest.filter((w) => w.days == null);
    return { pastWithDays: withDays, pastWeeklyOnly: weeklyOnly };
  }, [weeks, currentMonday]);

  return (
    <section className="space-y-3">
      <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400">
        VS history
      </h2>
      {weeks.length === 0 ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-6 text-center text-sm text-slate-500">
          No VS weeks recorded yet.
        </div>
      ) : (
        <>
          {pastWithDays.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Past weeks
              </div>
              <div className="space-y-3">
                {pastWithDays.map((w) => (
                  <VsWeekBox
                    key={w.id}
                    weekLabel={w.weekStart}
                    meta={`${w.opponentName} · ${w.opponentServer}`}
                    notes={w.notes}
                    kind={w.kind}
                    outcome={w.outcome}
                    policy={policy}
                    weekScoreM={w.weekScoreM}
                    twentiethM={w.twentiethM}
                    days={w.days}
                    rank={w.rank}
                    totalPlayers={w.totalPlayers}
                    othersAvgM={w.othersAvgM}
                  />
                ))}
              </div>
            </div>
          )}
          {pastWeeklyOnly.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="flex items-center gap-2">
                <div className="h-px flex-1 bg-slate-800" />
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Weekly only
                </div>
                <div className="h-px flex-1 bg-slate-800" />
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2 text-[11px] text-slate-400">
                Up to {DAILY_TRACKING_STARTS}, VS scores were only recorded once
                per week. Daily breakdowns start with the following week.
              </div>
              <div className="space-y-3">
                {pastWeeklyOnly.map((w) => (
                  <VsWeekBox
                    key={w.id}
                    weekLabel={w.weekStart}
                    meta={`${w.opponentName} · ${w.opponentServer}`}
                    notes={w.notes}
                    kind={w.kind}
                    outcome={w.outcome}
                    policy={policy}
                    weekScoreM={w.weekScoreM}
                    twentiethM={w.twentiethM}
                    days={w.days}
                    rank={w.rank}
                    totalPlayers={w.totalPlayers}
                    othersAvgM={w.othersAvgM}
                  />
                ))}
              </div>
            </div>
          )}
          <VsLegend />
        </>
      )}
    </section>
  );
}

function EventHistoryList({ rows }: { rows: SelfReportMeEventRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-6 text-center text-sm text-slate-500">
        No events in range.
      </div>
    );
  }
  return (
    <div className="divide-y divide-slate-800/70 overflow-hidden rounded-lg border border-slate-800 bg-slate-900/40">
      {rows.map((r) => (
        <EventHistoryRow key={r.eventId} row={r} />
      ))}
    </div>
  );
}

function EventHistoryRow({ row }: { row: SelfReportMeEventRow }) {
  const shortType =
    row.type === 'canyon_storm'
      ? 'CS'
      : row.type === 'desert_storm'
        ? 'DS'
        : 'SPEC';
  const statusBadge = statusFor(row);
  const sub =
    row.type === 'special_ingame'
      ? row.teamIndex != null
        ? `${row.eventDate} · Team ${row.teamIndex}`
        : row.eventDate
      : `${row.eventDate}${row.slot ? ` · Slot ${row.slot}` : ''}`;
  return (
    <div className="flex items-center gap-3 px-3 py-2 text-sm">
      <div className="w-10 shrink-0 text-xs font-semibold text-slate-400">
        {shortType}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-slate-200">
          {row.customName || row.eventDate}
        </div>
        <div className="text-[11px] text-slate-500">{sub}</div>
      </div>
      <span
        className={cn(
          'shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
          statusBadge.className,
        )}
      >
        {statusBadge.label}
      </span>
    </div>
  );
}

function statusFor(row: SelfReportMeEventRow): { label: string; className: string } {
  if (row.type === 'special_ingame') {
    if (row.outcome === 'upcoming') {
      return {
        label: 'Upcoming',
        className: 'bg-slate-800/70 text-slate-300',
      };
    }
    if (row.outcome === 'not_applied') {
      return {
        label: 'Not assigned',
        className: 'bg-slate-800/70 text-slate-400',
      };
    }
    // picked = assigned
    if (row.attendance === 'no_show') {
      return { label: 'No-show', className: 'bg-red-900/60 text-red-200' };
    }
    if (row.attendance === 'rulebreaker') {
      return { label: 'Rulebreaker', className: 'bg-amber-900/60 text-amber-200' };
    }
    if (row.attendance === 'showed') {
      return { label: 'Showed', className: 'bg-emerald-900/60 text-emerald-100' };
    }
    return { label: 'Assigned', className: 'bg-indigo-900/60 text-indigo-100' };
  }
  if (row.outcome === 'upcoming') {
    return {
      label: row.role === 'starter' ? 'Upcoming starter' : row.role === 'sub' ? 'Upcoming sub' : 'Upcoming',
      className: 'bg-slate-800/70 text-slate-300',
    };
  }
  if (row.outcome === 'not_applied') {
    return { label: 'Not registered', className: 'bg-slate-800/70 text-slate-400' };
  }
  if (row.outcome === 'applied_not_picked') {
    return { label: 'Not picked', className: 'bg-amber-900/50 text-amber-200' };
  }
  // picked
  if (row.attendance === 'no_show') {
    return { label: 'No-show', className: 'bg-red-900/60 text-red-200' };
  }
  if (row.attendance === 'late') {
    return { label: 'Late', className: 'bg-amber-900/60 text-amber-200' };
  }
  if (row.role === 'sub') {
    return { label: 'Sub', className: 'bg-indigo-900/60 text-indigo-100' };
  }
  return { label: 'Starter', className: 'bg-emerald-900/60 text-emerald-100' };
}

function VsLegend() {
  return (
    <div className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-3 text-[11px] text-slate-400">
      <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
        Legend
      </div>
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        <LegendItem
          swatch={<div className="h-4 w-4 rounded bg-emerald-700/80" />}
          text="Day OK"
        />
        <LegendItem
          swatch={
            <div className="flex h-4 w-4 items-center justify-center rounded bg-rose-900/90 ring-1 ring-rose-300">
              <AlertTriangle size={9} strokeWidth={3} className="text-white" />
            </div>
          }
          text={`Below ${DAILY_RED_ABSOLUTE_M} M in that day`}
        />
        <LegendItem
          swatch={
            <div className="flex h-4 w-4 items-center justify-center rounded bg-emerald-700/80">
              <Sparkles size={9} strokeWidth={2.5} className="text-yellow-100" />
            </div>
          }
          text="Day winner"
        />
        <LegendItem
          swatch={<div className="h-4 w-4 rounded bg-slate-800/70 text-slate-500" />}
          text="No score for that day"
        />
        <LegendItem
          swatch={<div className="h-4 w-6 rounded border border-indigo-500/70" />}
          text="Push week"
        />
        <LegendItem
          swatch={<div className="h-4 w-6 rounded border border-slate-600/70" />}
          text="Save week"
        />
        <LegendItem
          swatch={
            <div className="h-4 w-6 rounded border border-dashed border-rose-500/70" />
          }
          text="Aborted push"
        />
        <LegendItem
          swatch={
            <div className="h-4 w-6 rounded border border-slate-600/70 ring-2 ring-emerald-500/60" />
          }
          text="Won the week"
        />
        <LegendItem
          swatch={
            <div className="h-4 w-6 rounded border border-slate-600/70 ring-2 ring-red-600/60" />
          }
          text="Lost the week"
        />
        <LegendItem
          swatch={
            <div className="flex h-4 items-center justify-center rounded bg-slate-800/70 px-1 text-[8px] tabular-nums text-slate-400">
              12.3
            </div>
          }
          text="Score in millions per day"
        />
      </div>
    </div>
  );
}

function LegendItem({
  swatch,
  text,
}: {
  swatch: React.ReactNode;
  text: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="shrink-0">{swatch}</span>
      <span>{text}</span>
    </div>
  );
}
